import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	type DragOverEvent,
	type DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { domMax, LazyMotion } from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { ListContext } from "./list-context";
import { LIST_TOKENS } from "./list-tokens";
import type { ListProps } from "./list-types";

const DND_ACTIVATION_DISTANCE = 8;

function resolveItemId(item: unknown): string {
	if (item && typeof item === "object") {
		const obj = item as Record<string, unknown>;
		return String(obj.id ?? obj.value ?? item);
	}
	return String(item);
}

function isListItem(child: React.ReactNode): child is React.ReactElement {
	return (
		React.isValidElement(child) &&
		child.props != null &&
		typeof child.props === "object" &&
		"value" in (child.props as Record<string, unknown>)
	);
}

const ListImpl = React.forwardRef<HTMLUListElement, ListProps>(
	(
		{
			className,
			variant = "baseline",
			listStyle = "standard",
			selectionMode = "none",
			value,
			defaultValue,
			onChange,
			reorderable = false,
			items,
			onReorder,
			outerRadius = LIST_TOKENS.shape.itemOuterRadius,
			innerRadius = LIST_TOKENS.shape.itemInnerRadius,
			children,
			...props
		},
		ref,
	) => {
		const listRef = React.useRef<HTMLUListElement | null>(null);
		const [draggedOrder, setDraggedOrder] = React.useState<string[] | null>(
			null,
		);

		React.useImperativeHandle(ref, () => listRef.current as HTMLUListElement);

		const [selectedState, setSelectedState] = React.useState<Set<string>>(
			() => {
				const initialValue = value ?? defaultValue ?? [];
				return new Set(
					Array.isArray(initialValue)
						? initialValue
						: initialValue
							? [initialValue]
							: [],
				);
			},
		);

		const isControlled = value !== undefined;
		const selectedValues = React.useMemo(() => {
			if (isControlled) {
				return new Set(Array.isArray(value) ? value : value ? [value] : []);
			}
			return selectedState;
		}, [isControlled, value, selectedState]);

		const onItemChange = React.useCallback(
			(itemValue: string, selected: boolean) => {
				const nextSet = new Set(selectedValues);

				if (selectionMode === "single-select") {
					nextSet.clear();
					if (selected) nextSet.add(itemValue);
				} else if (selectionMode === "multi-select") {
					if (selected) {
						nextSet.add(itemValue);
					} else {
						nextSet.delete(itemValue);
					}
				} else {
					return;
				}

				if (!isControlled) setSelectedState(nextSet);

				if (onChange) {
					onChange(
						selectionMode === "single-select"
							? Array.from(nextSet)[0] || ""
							: Array.from(nextSet),
					);
				}
			},
			[selectedValues, selectionMode, isControlled, onChange],
		);

		const sensors = useSensors(
			useSensor(PointerSensor, {
				activationConstraint: { distance: DND_ACTIVATION_DISTANCE },
			}),
			useSensor(KeyboardSensor, {
				coordinateGetter: sortableKeyboardCoordinates,
			}),
		);

		const itemIds = React.useMemo(
			() => (items ? items.map(resolveItemId) : []),
			[items],
		);

		const currentOrder = React.useMemo(
			() => draggedOrder || itemIds,
			[draggedOrder, itemIds],
		);

		const handleDragStart = (_event: DragStartEvent) => {
			// Snapshot from currentOrder (not itemIds) to stay consistent if drag starts
			// while a previous draggedOrder is still pending flush.
			setDraggedOrder(
				currentOrder.length > 0 ? [...currentOrder] : [...itemIds],
			);
		};

		const handleDragOver = (event: DragOverEvent) => {
			const { active, over } = event;
			if (!over || active.id === over.id || !draggedOrder) return;

			const activeIndex = draggedOrder.indexOf(String(active.id));
			const overIndex = draggedOrder.indexOf(String(over.id));

			// Guard: skip setState when order hasn't changed to avoid unnecessary re-renders
			if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
				setDraggedOrder((prev) =>
					prev ? arrayMove(prev, activeIndex, overIndex) : prev,
				);
			}
		};

		const handleDragEnd = (event: DragEndEvent) => {
			const { active, over } = event;

			// Commit reorder BEFORE resetting draggedOrder so both state updates are
			// batched by React 18 automatic batching. Without this, resetting first
			// causes a flash render where currentOrder snaps back to the old itemIds
			// for one frame before the parent's new `items` prop arrives.
			if (over && active.id !== over.id && items && onReorder) {
				const activeIndex = items.findIndex(
					(item) => resolveItemId(item) === active.id,
				);
				const overIndex = items.findIndex(
					(item) => resolveItemId(item) === over.id,
				);

				if (activeIndex !== -1 && overIndex !== -1) {
					onReorder(arrayMove(items, activeIndex, overIndex));
				}
			}

			setDraggedOrder(null);
		};

		const handleDragCancel = () => {
			setDraggedOrder(null);
		};

		const handleKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
			if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;

			const listEl = listRef.current;
			if (!listEl) return;

			const focusableItems = Array.from(
				listEl.querySelectorAll<HTMLElement>(
					'.md3-list-item-focusable:not([aria-disabled="true"])',
				),
			);

			if (focusableItems.length === 0) return;

			e.preventDefault();

			const activeEl = document.activeElement as HTMLElement;
			const activeIndex = focusableItems.indexOf(activeEl);

			let nextIndex: number;
			if (activeIndex === -1) {
				nextIndex = e.key === "ArrowDown" ? 0 : focusableItems.length - 1;
			} else {
				nextIndex =
					e.key === "ArrowDown"
						? (activeIndex + 1) % focusableItems.length
						: (activeIndex - 1 + focusableItems.length) % focusableItems.length;
			}

			focusableItems[nextIndex]?.focus();
		};

		const childrenArray = React.Children.toArray(children);
		const listItemCount = childrenArray.filter(isListItem).length;

		let currentListItemIndex = 0;
		const processedChildren = React.Children.map(children, (child) => {
			if (!isListItem(child)) return child;

			const childValue = (child.props as Record<string, unknown>).value;
			const index =
				reorderable && currentOrder.length > 0
					? currentOrder.indexOf(String(childValue))
					: currentListItemIndex;

			currentListItemIndex++;
			return React.cloneElement(
				child as React.ReactElement<{ _listIndex?: number }>,
				{ _listIndex: index },
			);
		});

		const contextValue = React.useMemo(
			() => ({
				selectionMode,
				variant,
				listStyle,
				selectedValues,
				onItemChange,
				reorderable,
				itemCount: listItemCount,
				outerRadius,
				innerRadius,
			}),
			[
				selectionMode,
				variant,
				listStyle,
				selectedValues,
				onItemChange,
				reorderable,
				listItemCount,
				outerRadius,
				innerRadius,
			],
		);

		const dndId = React.useId();

		const isListbox =
			selectionMode === "single-select" || selectionMode === "multi-select";
		const role = isListbox ? "listbox" : "list";
		const ariaMultiselectable =
			selectionMode === "multi-select" ? true : undefined;

		const baseClasses = cn(
			"flex flex-col w-full m-0 p-0 list-none bg-transparent focus:outline-none",
			listStyle === "segmented" && "gap-[2px]",
			className,
		);

		const renderedList = (
			// biome-ignore lint/a11y/useAriaPropsSupportedByRole: role is dynamically set to listbox when selectionMode is active
			<ul
				ref={listRef}
				role={role}
				aria-multiselectable={ariaMultiselectable}
				onKeyDown={handleKeyDown}
				className={baseClasses}
				{...props}
			>
				{processedChildren}
			</ul>
		);

		return (
			<LazyMotion features={domMax} strict>
				<ListContext.Provider value={contextValue}>
					{reorderable && items ? (
						<DndContext
							id={dndId}
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragStart={handleDragStart}
							onDragOver={handleDragOver}
							onDragEnd={handleDragEnd}
							onDragCancel={handleDragCancel}
						>
							<SortableContext
								items={currentOrder}
								strategy={verticalListSortingStrategy}
							>
								{renderedList}
							</SortableContext>
						</DndContext>
					) : (
						renderedList
					)}
				</ListContext.Provider>
			</LazyMotion>
		);
	},
);

ListImpl.displayName = "List";

export const List = React.memo(ListImpl);
export type { ListProps };
